import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../api/client';
import { MaskaTelefona } from '../hodba/MaskaTelefona';
import { useAuth } from '../auth/useAuth';
import { usePermissions } from '../auth/usePermissions';
import { ShareCallButton } from './ShareCallButton';
import { formatDuration } from './statusVocabulary';
import type { CallRow } from './callApi';

/**
 * Карточка звонка (ТЗ §4.4, карта 3.3).
 *
 * ⭐ Появилась по слову владельца 12.08: «где итоговая расшифровка, не могу
 * найти — в истории звонков только список». Так и было: конвейер писал
 * эталонную расшифровку и запись в базу, отдавал их запросом карточки, а
 * открыть карточку с экрана было нечем. Текст, который никому не показан,
 * ничем не отличается от ненаписанного.
 *
 * Запись играет ПОТОКОМ через наш же запрос (с правом `tel.records.listen`),
 * а не ссылкой на файл: публичной ссылки на разговор клиента не создаётся ни
 * на секунду — её потом не отозвать, и живёт она в переписках и логах прокси.
 */
interface Replica {
  role: string;
  t_from: number;
  t_to: number;
  text: string;
}

interface CardPayload {
  call: CallRow;
  recording: {
    id: number;
    duration_sec: number | null;
    tier: string;
    available_now: boolean;
    moved_at: string | null;
  } | null;
  transcript: { status: string | null; replicas: Replica[] } | null;
  attempts: { at: string | null; result: string | null; ring_sec: number | null; sip_code: number | null }[];
}

async function fetchCard(id: number): Promise<CardPayload> {
  const { data } = await apiClient.get<CardPayload>(`/telephony/calls/${id}`);

  return data;
}

export function CallCard({ callId, onClose }: { callId: number; onClose: () => void }) {
  const { can } = usePermissions();
  const { user } = useAuth();
  const qc = useQueryClient();
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [audioError, setAudioError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({ queryKey: ['telephony', 'call', callId], queryFn: () => fetchCard(callId) });

  const mayListen = can('tel.records.listen');
  const playable = Boolean(data?.recording?.available_now) && mayListen;

  /*
   * Запись забирается тем же запросом, что и всё остальное, и играется из
   * памяти браузера. Тег <audio src=…> к чужому домену куки не отправит —
   * и человек получил бы «нет доступа» на своей же записи.
   */
  useEffect(() => {
    if (!playable) {
      return;
    }

    let revoke: string | null = null;
    let cancelled = false;

    void (async () => {
      try {
        const { data: blob } = await apiClient.get<Blob>(`/telephony/calls/${callId}/audio`, { responseType: 'blob' });

        if (cancelled) {
          return;
        }

        revoke = URL.createObjectURL(blob);
        setAudioUrl(revoke);
      } catch {
        if (!cancelled) {
          setAudioError('Запись не отдалась — попробуйте ещё раз или скажите об этом.');
        }
      }
    })();

    return () => {
      cancelled = true;

      if (revoke) {
        URL.revokeObjectURL(revoke);
      }
    };
  }, [callId, playable]);

  const restore = useMutation({
    mutationFn: async () => apiClient.post(`/telephony/calls/${callId}/recording/restore`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['telephony', 'call', callId] }),
  });

  if (isLoading || !data) {
    return (
      <Panel onClose={onClose}>
        <p className="text-ops-caption" style={{ color: 'var(--ops-text-secondary)' }}>Открываю разговор…</p>
      </Panel>
    );
  }

  const row = data.call;
  const mine = row.manager_id !== null && row.manager_id === user?.id;

  return (
    <Panel onClose={onClose}>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        {/* zhur1: карточка звонка — номер краями, раскрытие кликом. */}
        <span className="text-ops-h2 font-bold">
          <MaskaTelefona masked={row.client_phone || null} entityType="tel_calls" entityId={callId} pusto="номер неизвестен" />
        </span>
        <span className="text-ops-caption" style={{ color: 'var(--ops-text-secondary)' }}>
          {row.at ? new Date(row.at).toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
          {' · '}
          {row.direction === 'inbound' ? 'входящий' : 'исходящий'}
          {row.line ? ` · линия ${row.line.name}` : ''}
          {' · '}
          {formatDuration(row.talk_sec ?? 0)}
        </span>

        <span className="ml-auto">
          {can('tel.calls.share_tg') ? <ShareCallButton callId={callId} /> : null}
        </span>
      </div>

      {/* Запись. Три честных состояния: играет · в архиве · права нет. */}
      <div className="mt-3">
        {data.recording === null ? (
          <Muted>Записи у этого разговора нет.</Muted>
        ) : !mayListen ? (
          <Muted>Слушать записи не входит в ваши права.</Muted>
        ) : data.recording.available_now ? (
          audioUrl ? (
            <audio controls src={audioUrl} className="w-full" />
          ) : (
            <Muted>{audioError ?? 'Достаю запись…'}</Muted>
          )
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <Muted>Запись в архиве — на сервере её сейчас нет.</Muted>
            <button
              type="button"
              disabled={restore.isPending}
              onClick={() => restore.mutate()}
              className="rounded-ops-sm px-3 py-1 text-ops-label font-semibold disabled:opacity-50"
              style={{ background: 'var(--ops-primary)', color: '#fff' }}
            >
              {restore.isPending ? 'Возвращаю…' : 'Вернуть из архива'}
            </button>
          </div>
        )}
      </div>

      {/* Расшифровка — эталон журнала, а не живая лента. */}
      <div className="mt-4">
        <div className="text-ops-micro font-bold uppercase tracking-[0.08em]" style={{ color: 'var(--ops-text-tertiary)' }}>
          Расшифровка разговора
        </div>

        {data.transcript === null || data.transcript.replicas.length === 0 ? (
          <Muted>
            {row.transcript_status === 'pending'
              ? 'Расшифровка ещё считается — появится здесь сама.'
              : 'Расшифровки у этого разговора нет.'}
          </Muted>
        ) : (
          <div className="mt-2 max-h-96 space-y-1.5 overflow-y-auto rounded-ops-md p-3"
            style={{ background: 'var(--ops-surface-2)', border: '1px solid var(--ops-border)' }}
          >
            {data.transcript.replicas.map((replica, index) => (
              <div key={`${replica.t_from}-${index}`} className="text-ops-caption">
                <span className="mr-2 tabular-nums" style={{ color: 'var(--ops-text-tertiary)' }}>
                  {stamp(replica.t_from)}
                </span>
                <span className="mr-2 font-semibold" style={{ color: color(replica.role) }}>
                  {label(replica.role, mine ? user?.name ?? 'Менеджер' : 'Менеджер', row.client_phone)}
                </span>
                <span>{replica.text}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Итоги трёх уровней (§2.13) — кто именно так решил. */}
      <div className="mt-4 grid gap-1 text-ops-caption sm:grid-cols-3">
        <div>
          <b>Автоматика:</b> {autoWord(row.outcome.auto)}
        </div>
        <div>
          <b>Менеджер:</b> {row.outcome.business_outcome?.name ?? '—'}
        </div>
        <div style={{ color: 'var(--ops-text-tertiary)' }}>
          <b>ИИ-итог:</b> задел, выключен
        </div>
      </div>

      {/* Попытки дозвона (§1.5). У ручного звонка попытка одна — так и видно. */}
      {data.attempts.length > 0 ? (
        <div className="mt-3 text-ops-caption">
          <span className="text-ops-micro font-bold uppercase tracking-[0.08em]" style={{ color: 'var(--ops-text-tertiary)' }}>
            Попытки
          </span>
          <ul className="mt-1 space-y-0.5">
            {data.attempts.map((attempt, index) => (
              <li key={index} style={{ color: 'var(--ops-text-secondary)' }}>
                {attempt.at ? new Date(attempt.at).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }) : '—'}
                {' · '}
                {attempt.result ?? 'без результата'}
                {attempt.ring_sec !== null ? ` · гудки ${attempt.ring_sec} с` : ''}
                {attempt.sip_code !== null ? ` · код ${attempt.sip_code}` : ''}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </Panel>
  );
}

function Panel({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div
      data-tel="call-card"
      className="mt-3 rounded-ops-md p-3"
      style={{ background: 'var(--ops-surface)', border: '1px solid var(--ops-border-strong)' }}
    >
      <div className="flex justify-end">
        <button
          type="button"
          onClick={onClose}
          className="rounded-ops-sm px-2 py-0.5 text-ops-caption"
          style={{ color: 'var(--ops-text-secondary)' }}
        >
          Закрыть ✕
        </button>
      </div>
      {children}
    </div>
  );
}

/** Подпись реплики — именем и номером, как в живой ленте (жалоба 12.08). */
function label(role: string, operatorName: string, clientPhone: string): string {
  if (role === 'client') return `${formatPhone(clientPhone)}:`;
  // Голоса слиплись (эхо динамика) — честно говорим, что не разобрать, кто
  // именно сказал: подпись «Разговор» владелец 13.08 прочитал как имя.
  if (role === 'mixed') return 'Не разобрать кто:';

  return `${operatorName.split(/\s+/)[0]}:`;
}

function color(role: string): string {
  if (role === 'client') return 'var(--ops-primary)';
  if (role === 'mixed') return 'var(--ops-text-secondary)';

  return 'var(--ops-success)';
}

function autoWord(auto: string | null): string {
  switch (auto) {
    case 'success': return 'разговор состоялся';
    case 'short': return 'короткий';
    case 'machine': return 'автоответчик';
    case 'no_answer': return 'не ответили';
    default: return '—';
  }
}

function stamp(seconds: number): string {
  const total = Math.max(0, Math.round(seconds));

  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

function formatPhone(raw: string): string {
  const digits = (raw ?? '').replace(/\D+/g, '');

  if (digits.length === 11) {
    return `+${digits[0]} ${digits.slice(1, 4)} ${digits.slice(4, 7)}-${digits.slice(7, 9)}-${digits.slice(9)}`;
  }

  return raw;
}

function Muted({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-ops-caption" style={{ color: 'var(--ops-text-secondary)' }}>
      {children}
    </p>
  );
}
